import { type ComponentType, type CSSProperties } from 'react';
import { Icon } from '../../components/Icon';
import { useI18n } from '../../i18n/i18n-context';
import type { FeatureId } from '../promo/promos';

// Small, quiet pictures of each feature: the app's own words and shapes, with the writer's text
// drawn as bars. They loop only while on screen (home.css), and stand still without motion.

const vars = (values: Record<string, string | number>) => values as CSSProperties;

/** A line of text, as a bar `width` long. */
function Bar({ width, className }: { readonly width: string; readonly className?: string }) {
  return <span className={className ?? 'art-bar'} style={vars({ '--w': width })} />;
}

function WriteArt() {
  const { t } = useI18n();
  return (
    <div className="art-panel art-write">
      <div className="art-write-lines">
        <Bar width="94%" />
        <Bar width="78%" />
        <span className="art-write-last">
          <Bar width="52%" className="art-bar art-bar--typing" />
          <span className="art-caret" />
        </span>
      </div>
      <div className="art-write-foot">
        {(['sparkle', 'message', 'check'] as const).map((icon, index) => (
          <span key={icon} className="art-source" style={vars({ '--i': index })}>
            <Icon name={icon} />
          </span>
        ))}
        <span className="art-rewrite">
          {t.options.instruction}
          <Icon name="sparkle" />
        </span>
      </div>
    </div>
  );
}

const TIERS = [
  'minorGrammarFixes',
  'naturalRewording',
  'enhancedClarity',
  'optimalVersion',
] as const;

function VersionsArt() {
  const { t } = useI18n();
  return (
    <ol className="art-versions">
      {TIERS.map((tier, index) => (
        <li
          key={tier}
          className="art-panel art-version"
          style={vars({ '--i': index, '--tier-opacity': 0.25 + 0.25 * index })}
        >
          <span className="art-version-head">
            <span className="art-version-number">{String(index + 1).padStart(2, '0')}</span>
            <span className="art-version-name">{t.tiers[tier].label}</span>
          </span>
          <Bar width={`${String(92 - index * 4)}%`} />
          <Bar width={`${String(64 + index * 7)}%`} />
        </li>
      ))}
    </ol>
  );
}

function LearnArt() {
  const { t } = useI18n();
  return (
    <div className="art-panel art-learn">
      <div className="art-diff">
        <span className="art-diff-line">
          <Bar width="22%" />
          <Bar width="18%" className="art-bar art-del" />
          <Bar width="24%" className="art-bar art-ins" />
          <Bar width="20%" />
        </span>
        <span className="art-diff-line">
          <Bar width="34%" />
          <Bar width="16%" className="art-bar art-del art-del--later" />
          <Bar width="20%" className="art-bar art-ins art-ins--later" />
        </span>
      </div>
      <div className="art-views">
        {[t.card.text, t.card.diff, t.card.why, t.card.alts].map((view, index) => (
          <span key={view} className="art-view" style={vars({ '--i': index })}>
            {view}
          </span>
        ))}
      </div>
    </div>
  );
}

function SteerArt() {
  const { t, format } = useI18n();
  const { tones, lengths } = t.options;
  const choices = [
    { icon: 'message', values: [tones.friendly, tones.formal, tones.confident] },
    { icon: 'lines', values: [lengths.same, lengths.shorter, lengths.longer] },
    {
      icon: 'globe',
      values: [format.language('en'), format.language('es'), format.language('ja')],
    },
  ] as const;
  return (
    <div className="art-steer">
      <div className="art-toolbar">
        {choices.map(({ icon, values }, option) => (
          <span key={icon} className="art-option" style={vars({ '--o': option })}>
            <Icon name={icon} />
            <span className="art-cycle">
              {values.map((value, index) => (
                <span key={value} style={vars({ '--i': index })}>
                  {value}
                </span>
              ))}
            </span>
          </span>
        ))}
      </div>
      <div className="art-panel art-steer-card" style={vars({ '--tier-opacity': 1 })}>
        <span className="art-version-head">
          <span className="art-version-number">04</span>
          <span className="art-version-name">{t.tiers.optimalVersion.label}</span>
        </span>
        <Bar width="88%" className="art-bar art-bar--steer" />
        <Bar width="60%" className="art-bar art-bar--steer art-bar--later" />
      </div>
    </div>
  );
}

function ToolsArt() {
  const { t } = useI18n();
  const { tools } = t.aiTools;
  const results = [
    { icon: 'bullets', name: tools.bullets.name, rows: 3 },
    { icon: 'checklist', name: tools.checklist.name, rows: 3 },
    { icon: 'summary', name: tools.summary.name, rows: 0 },
  ] as const;
  return (
    <div className="art-tools">
      <div className="art-panel art-tools-draft">
        <Bar width="96%" />
        <Bar width="90%" />
        <Bar width="94%" />
        <Bar width="58%" />
      </div>
      <span className="art-tools-wand">
        <Icon name="wand" />
      </span>
      <div className="art-panel art-tools-result art-cycle">
        {results.map(({ icon, name, rows }, index) => (
          <div key={icon} className="art-tools-state" style={vars({ '--i': index })}>
            <span className="art-tools-name">
              <Icon name={icon} />
              {name}
            </span>
            {rows > 0 ? (
              <span className={`art-tools-rows art-tools-rows--${icon}`}>
                {[78, 62, 70].map((width, row) => (
                  <span key={width} className="art-tools-row" style={vars({ '--row': row })}>
                    <span className="art-tools-marker" />
                    <Bar width={`${String(width)}%`} />
                  </span>
                ))}
              </span>
            ) : (
              <span className="art-tools-rows">
                <Bar width="92%" />
                <Bar width="66%" />
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AnywhereArt() {
  return (
    <div className="art-anywhere">
      <div className="art-panel art-voice">
        <span className="art-voice-icon art-voice-icon--mic">
          <Icon name="microphone" />
        </span>
        <span className="art-wave">
          {[0.35, 0.6, 0.9, 0.55, 0.75, 1, 0.65, 0.4, 0.8, 0.5, 0.3, 0.6].map((height, index) => (
            <span key={index} style={vars({ '--h': height, '--i': index })} />
          ))}
        </span>
        <span className="art-voice-icon">
          <Icon name="speaker" />
        </span>
      </div>
      <div className="art-panel art-write">
        <Bar width="94%" />
        <Bar width="78%" />
        <Bar width="52%" className="art-bar art-bar--typing" />
      </div>
    </div>
  );
}

function RewriteArt() {
  return (
    <div className="art-rewrite-preview">
      <VersionsArt />
      <LearnArt />
    </div>
  );
}

function ImageTextArt() {
  return (
    <div className="art-image-text">
      <div className="art-panel art-image-source">
        <span className="art-image-icon">
          <Icon name="image" />
        </span>
        <div className="art-image-lines">
          <Bar width="86%" />
          <Bar width="96%" />
          <Bar width="64%" />
        </div>
        <span className="art-image-scan" />
      </div>
      <span className="art-tools-wand">
        <Icon name="sparkle" />
      </span>
      <div className="art-panel art-image-result">
        <Bar width="86%" />
        <Bar width="96%" />
        <span className="art-write-last">
          <Bar width="64%" />
          <span className="art-caret" />
        </span>
      </div>
    </div>
  );
}

const ART: Readonly<Record<FeatureId, ComponentType>> = {
  rewrite: RewriteArt,
  tones: SteerArt,
  instructions: WriteArt,
  tools: ToolsArt,
  dictation: AnywhereArt,
  'image-text': ImageTextArt,
};

/** The picture for a feature, for its card on the home page. Decorative: the card says it all. */
export function FeatureArt({ feature }: { readonly feature: FeatureId }) {
  const Art = ART[feature];
  return <Art />;
}
